import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  ShadowDomWrapper,
  generateConciergeCSSFromWidgetTheme,
  useConversationStarters,
  useWidgetVisibilityTracking,
} from "@brandclub/common-ui";
import {
  useWidgetTheme,
  ConversationStartersWidget,
} from "@brandclub/bc-widgets";
import { useCurrentStacklineSku } from "../../hooks/useCurrentStacklineSku";
import { HOST_DOMAIN } from "../../utils/domain";

// common-ui hooks expect the origin only — they prepend /api-rewards internally
const API_BASE_URL = HOST_DOMAIN;

/**
 * Hook to observe DOM for elements matching a selector
 */
const useElementObserver = (selector: string, once = true) => {
  const [element, setElement] = useState<Element | null>(null);

  useEffect(() => {
    const tryFind = () => {
      const el = document.querySelector(selector);
      if (el) {
        setElement(el);
        return true;
      } else {
        setElement(null);
        return false;
      }
    };

    if (tryFind() && once) return;

    const observer = new MutationObserver(() => {
      const found = tryFind();
      if (found && once) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [selector, once]);

  return element;
};

interface ConversationStartersPortalProps {
  isConciergeEnabled: boolean;
  /** Callback when a question is clicked - passed from parent to avoid cross-bundle Zustand issues */
  onQuestionClick: (question: string, stacklineSku: string) => void;
  appName?: string;
  appId?: string;
}

/**
 * Portal component that renders ConversationStarters into brand-provided div
 * with class "bc-concierge-suggested-questions"
 * Uses Shadow DOM for style isolation and applies Concierge theme from bc-widgets
 */
export const ConversationStartersPortal: React.FC<
  ConversationStartersPortalProps
> = ({ isConciergeEnabled, onQuestionClick, appName, appId }) => {
  const suggestedQuestionsElement = useElementObserver(
    ".bc-concierge-suggested-questions"
  );
  const currentStacklineSku = useCurrentStacklineSku();

  // Get theme from bc-widgets theme system
  const { data: widgetThemeData } = useWidgetTheme();

  // Generate theme CSS directly from bc-widgets theme
  const themeCSS = useMemo(() => {
    return generateConciergeCSSFromWidgetTheme(widgetThemeData);
  }, [widgetThemeData]);

  // Fetch questions using the shared hook from common-ui
  const { questions, isLoading } = useConversationStarters({
    stacklineSku: currentStacklineSku || undefined,
    apiBaseUrl: API_BASE_URL,
    enabled: isConciergeEnabled && !!currentStacklineSku,
  });

  // Track conversation starters widget visibility for pageview metrics
  const { trackImmediate } = useWidgetVisibilityTracking({
    appName,
    appId,
    widgetType: 'embedded',
    enabled: isConciergeEnabled && !!currentStacklineSku,
  });

  useEffect(() => {
    if (suggestedQuestionsElement && currentStacklineSku && isConciergeEnabled) {
      trackImmediate();
    }
  }, [suggestedQuestionsElement, currentStacklineSku, isConciergeEnabled, trackImmediate]);

  // Handle question click - call parent callback with stacklineSku
  const handleQuestionClick = (question: string) => {
    if (currentStacklineSku) {
      onQuestionClick(question, currentStacklineSku);
    }
  };

  // Don't render if feature is disabled or no element found or no SKU
  if (
    !isConciergeEnabled ||
    !suggestedQuestionsElement ||
    !currentStacklineSku
  ) {
    return null;
  }

  return createPortal(
    <ShadowDomWrapper
      themeCSS={themeCSS}
      hostId="conversation-starters-shadow-host"
    >
      <ConversationStartersWidget
        widgetId="pdp-conversation-starters"
        displayName="Conversation Starters"
        questions={questions}
        isLoading={isLoading}
        onQuestionClick={handleQuestionClick}
      />
    </ShadowDomWrapper>,
    suggestedQuestionsElement
  );
};
